/**
 * Checking a view before it draws. Every issue says where it is and what to do, in words an agent can act on:
 * `body[1].children[0].value: should be a number, not string "47%"`.
 */
import { COMPONENT_TYPES, component, type ComponentDef } from './catalog.ts'
import { sandboxWarnings } from './sandbox.ts'
import { checkFields, isSafeImage, isSafeLink, type CheckContext, type Issue } from './schema.ts'

export type { Issue }

const MAX_ISSUES = 50
const MAX_VIEW_LENGTH = 100_000
/** A view is built from the catalog: its own components are for the few pieces nothing in it can show. */
export const MAX_CUSTOM = 3
const NAME = /^[A-Za-z_][\w-]*$/

/** Names agents reach for, and the component that does it. */
const COMPONENT_ALIASES: Readonly<Record<string, string>> = {
  card: 'frame',
  box: 'frame',
  panel: 'frame',
  container: 'stack',
  column: 'stack',
  row: 'stack',
  flex: 'stack',
  hstack: 'stack',
  vstack: 'stack',
  graph: 'chart',
  plot: 'chart',
  stat: 'metric',
  kpi: 'metric',
  number: 'metric',
  title: 'heading',
  header: 'heading',
  h1: 'heading',
  paragraph: 'text',
  p: 'text',
  label: 'badge',
  tag: 'badge',
  chip: 'badge',
  pill: 'badge',
  link: 'button',
  image: 'avatar',
  each: 'repeat',
  for: 'repeat',
  map: 'repeat',
  rows: 'list',
  links: 'nav',
}

function unknownType(type: string): string {
  const alias = COMPONENT_ALIASES[type.toLowerCase()]
  const hint = alias ? ` Use "${alias}".` : ''
  return `there's no "${type}" component.${hint} The components are ${COMPONENT_TYPES.join(', ')}.`
}

/** Checks a whole view. No issues means it draws as written. */
export function validateView(view: unknown): Issue[] {
  const issues: Issue[] = []
  if (!view || typeof view !== 'object' || Array.isArray(view))
    return [{ path: '', message: 'a view is an object: { "version": 1, "body": [ … ] }' }]
  const v = view as Record<string, unknown>
  for (const key of Object.keys(v)) {
    if (key === 'data')
      issues.push({ path: key, message: 'a view reads nothing here: write the values into its components' })
    else if (!['version', 'title', 'body'].includes(key))
      issues.push({ path: key, message: "isn't part of a view; a view has version, title, and body" })
  }
  if (v.version !== 1) issues.push({ path: 'version', message: 'should be 1' })
  if (v.title !== undefined && (typeof v.title !== 'string' || v.title.length > 80))
    issues.push({ path: 'title', message: 'should be text, at most 80 characters' })
  if (JSON.stringify(view).length > MAX_VIEW_LENGTH)
    issues.push({ path: '', message: `is too long; a view is at most ${MAX_VIEW_LENGTH} characters` })
  if (!Array.isArray(v.body))
    issues.push({ path: 'body', message: 'should be a list of nodes: [ { "type": "heading", "text": "…" }, … ]' })
  else {
    const ids = new Set<string>()
    v.body.forEach((n, i) => checkNode(n, `body[${i}]`, new Set(), issues, ids))
    const own = countCustom(v.body)
    if (own > MAX_CUSTOM)
      issues.push({
        path: 'body',
        message: `has ${own} custom components; at most ${MAX_CUSTOM}. Build it from the catalog's components.`,
      })
  }
  return issues.slice(0, MAX_ISSUES)
}

function checkNode(value: unknown, path: string, scope: ReadonlySet<string>, issues: Issue[], ids: Set<string>) {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return void issues.push({ path, message: 'should be a node: { "type": "<component>", … }' })
  const node = value as Record<string, unknown>
  if (typeof node.type !== 'string')
    return void issues.push({
      path: `${path}.type`,
      message: `is required: which component this is (${COMPONENT_TYPES.join(', ')})`,
    })
  const def = component(node.type)
  if (!def) return void issues.push({ path: `${path}.type`, message: unknownType(node.type) })
  if (node.id !== undefined) {
    if (typeof node.id !== 'string' || !NAME.test(node.id))
      issues.push({ path: `${path}.id`, message: 'should be a name: letters, digits, _ or -' })
    else if (ids.has(node.id))
      issues.push({ path: `${path}.id`, message: `"${node.id}" is already used; ids are unique in a view` })
    else ids.add(node.id)
  }
  // A repeat's template can also bind to its item.
  const inner = def.type === 'repeat' ? new Set([...scope, typeof node.as === 'string' ? node.as : 'item']) : scope
  const ctx: CheckContext = { issues, scope, node: (v, p) => checkNode(v, p, inner, issues, ids) }
  checkFields(def.props, node, path, ctx, ['type', 'id'])
  checkAddresses(def.type, node, path, issues)
  if (def.type === 'custom') checkCustom(node, path, issues)
  // Copies multiply, so a small view can't ask for thousands of frames.
  if (def.type === 'repeat' && containsType(node.template, ['repeat', 'custom']))
    issues.push({ path: `${path}.template`, message: "can't hold a repeat or a custom component" })
}

/** Addresses are checked here and on what a repeat resolves, so one can't arrive through a binding. */
function checkAddresses(type: string, props: Record<string, unknown>, path: string, issues: Issue[]) {
  const at = (rest: string) => (path ? `${path}.${rest}` : rest)
  if (type === 'avatar' && typeof props.src === 'string' && !isSafeImage(props.src))
    issues.push({ path: at('src'), message: 'should be a path on this site; images from elsewhere are refused' })
  if ((type === 'list' || type === 'nav') && Array.isArray(props.items))
    props.items.forEach((item: unknown, i) => {
      const href = (item as { href?: unknown } | null)?.href
      if (typeof href === 'string' && !isSafeLink(href))
        issues.push({ path: at(`items[${i}].href`), message: 'should be a site path starting "/" or an https:// link' })
    })
}

/** A component of the agent's own: only with a reason no catalog component fits, and code the sandbox can run. */
function checkCustom(node: Record<string, unknown>, path: string, issues: Issue[]) {
  if (typeof node.reason === 'string' && node.reason.trim().length < 20)
    issues.push({
      path: `${path}.reason`,
      message: 'should say, in a sentence, which components you considered and why none of them fits',
    })
  if (typeof node.html === 'string')
    for (const warning of sandboxWarnings(node.html)) issues.push({ path: `${path}.html`, message: warning })
}

function containsType(value: unknown, types: string[]): boolean {
  if (Array.isArray(value)) return value.some((item) => containsType(item, types))

  if (!value || typeof value !== 'object') return false

  const node = value as Record<string, unknown>
  return types.includes(node.type as string) || Object.values(node).some((item) => containsType(item, types))
}

/** How many custom components are anywhere in the nodes (in frames, stacks, tabs, a repeat's template). */
function countCustom(value: unknown): number {
  if (Array.isArray(value)) return value.reduce((n: number, v) => n + countCustom(v), 0)
  if (!value || typeof value !== 'object') return 0
  const node = value as Record<string, unknown>
  return (
    (node.type === 'custom' ? 1 : 0) +
    Object.entries(node).reduce((n, [k, v]) => (k === 'props' && node.type === 'custom' ? n : n + countCustom(v)), 0)
  )
}

/** Checks a node's props once its bindings are read. Child nodes are checked as they draw. */
export function checkResolved(def: ComponentDef, props: Record<string, unknown>): Issue[] {
  const issues: Issue[] = []
  checkFields(def.props, props, '', { issues, scope: null, node: () => {} }, ['type', 'id'])
  checkAddresses(def.type, props, '', issues)
  return issues
}
